import { defineStore } from 'pinia';
import { shouldThrottle } from '@/util/rateLimit.js';
import apiClient from '@/util/apiClient';
import posthog from '@/composables/usePostHog.js';

const GROUP_DETAIL_TTL_MS = 120_000;

export interface CreateGroupDTO {
	groupNumber?: number | null;
	name?: string | null;
	shortName?: string | null;
	addressLine1?: string | null;
	addressLine2?: string | null;
	city?: string | null;
	state?: string | null;
	postalCode5?: string | null;
	postalCode4?: string | null;
	country?: string | null;
	groupStatus?: string | number | null;
	groupPlan?: string | number | null;
	groupOwner?: number | null;
}

export interface GroupDTO {
	groupNumber: number | null;
	name?: string | null;
	shortName?: string | null;
	description?: string | null;
	[key: string]: unknown;
}

export interface UpdateGroupDetailsPayload {
	name: string;
	shortName: string;
	description: string;
}

export interface GroupUserDTO {
	userNumber: number;
	firstName?: string | null;
	middleName?: string | null;
	lastName?: string | null;
	city?: string | null;
	state?: string | null;
	gender?: string | null;
	primaryPhone?: string | null;
	secondaryPhone?: string | null;
	primaryEmail?: string | null;
	secondaryEmail?: string | null;
	birthdate?: string | null;
	createdOn?: string | null;
	createdBy?: string | null;
	updatedOn?: string | null;
	username?: string | null;
	usernameConfirmed?: boolean | null;
	[key: string]: unknown;
}

export interface TableUserFilters {
	name?: string | null;
	email?: string | null;
	phone?: string | null;
	minAge?: number | null;
	maxAge?: number | null;
	gender?: string | null;
	city?: string | null;
	state?: string | null;
	license?: string | null;
	membership?: string | null;
}

export interface SearchUserOption {
	label: string;
	id: number;
	firstName?: string | null;
	middleName?: string | null;
	lastName?: string | null;
	city?: string | null;
	state?: string | null;
	userGender?: string | null;
	primaryPhone?: string | null;
	secondaryPhone?: string | null;
	primaryEmail?: string | null;
	secondaryEmail?: string | null;
	birthday?: string | null;
	createdOn?: string | null;
	createdBy?: string | null;
	updatedOn?: string | null;
	username?: string | null;
	usernameConfirmed?: boolean | null;
}

export interface GroupOwnerInvitePayload {
	email: string;
	firstName?: string;
	lastName?: string;
	roleName: string;
}
export interface CreateGroupPayload {
	group: CreateGroupDTO;
	groupOwnerInvite: GroupOwnerInvitePayload;
	addressConfirmationToken?: string;
}
export interface SendGroupOwnerInvitePayload {
	email: string;
	firstName?: string;
	lastName?: string;
	groupName: string;
}
export type SendGroupOwnerInviteResult =
	| {
			success: true;
			invitationIds?: number[];
			message?: string;
	  }
	| {
			success: false;
			message?: string;
			status?: number | undefined;
			error?: unknown;
	  };

interface ApiErrorPayload {
	message?: string;
	error?: string;
}
interface CachedGroupDetail {
	data: GroupDTO;
	ts: number;
}

interface GroupsState {
	group: GroupDTO | null;
	groups: GroupDTO[];
	users: GroupUserDTO[];
	searchUsers: SearchUserOption[];
	groupByNumberCache: Record<string, CachedGroupDetail>;
}
export interface EmployeeGroupUpdateRequest {
	group?: {
		name?: string | null;
		shortName?: string | null;
		description?: string | null;
		status?: string | null;
		groupOwnerUserNumber?: number | null;
		addressLine1?: string | null;
		addressLine2?: string | null;
		city?: string | null;
		state?: string | null;
		postalCode5?: string | null;
		postalCode4?: string | null;
		country?: string | null;
		addressConfirmationToken?: string | null;
	};
	settings?: {
		groupType?: string | null;
		groupStatus?: string | null;
		sport?: string | null;
		travelEnforcementMode?: string | null;
		defaultRank?: number | null;
	};
	entitlement?: {
		plan?: string | null;
		maxMembers?: number | null;
		maxRefereeAssignors?: number | null;
		maxRefereeAdmins?: number | null;
	};
}
export const useGroupsStore = defineStore('groupsStore', {
	state: (): GroupsState => ({
		group: null,
		groups: [],
		users: [],
		searchUsers: [],
		groupByNumberCache: {}
	}),
	actions: {
		async fetchAllGroups(): Promise<GroupDTO[] | undefined> {
			if (shouldThrottle('fetchAllGroups', 100)) {
				posthog.capture('throttled_action', { action: 'fetchAllGroups' });
				return;
			}

			const response = await apiClient.get<GroupDTO[]>('/groups/all');
			this.groups = Array.isArray(response.data) ? response.data : [];
			return this.groups;
		},
		async fetchAllEmployeeGroups(): Promise<GroupDTO[] | undefined> {
			if (shouldThrottle('fetchAllEmployeeGroups', 100)) {
				posthog.capture('throttled_action', { action: 'fetchAllEmployeeGroups' });
				return;
			}

			const response = await apiClient.get<GroupDTO[]>('/employee/groups');
			this.groups = Array.isArray(response.data) ? response.data : [];
			return this.groups;
		},
		async updateEmployeeGroup(groupNumber: number, payload: EmployeeGroupUpdateRequest) {
			if (!groupNumber) {
				throw new Error('Group number is required.');
			}
			if (Object.keys(payload.group ?? {}).length === 0 && Object.keys(payload.settings ?? {}).length === 0 && Object.keys(payload.entitlement ?? {}).length === 0) {
				throw new Error('At least one field is required to update a group.');
			}
			const response = await apiClient.patch<EmployeeGroupUpdateRequest>(`/employee/groups/${groupNumber}`, payload, {
				headers: {
					'Content-Type': 'application/merge-patch+json'
				}
			});
			const updatedGroup = response.data as (typeof this.groups)[number];
			if (updatedGroup.groupNumber != null) {
				const existingIndex = this.groups.findIndex((group) => group.groupNumber === updatedGroup.groupNumber);
				if (existingIndex >= 0) {
					this.groups.splice(existingIndex, 1, updatedGroup);
				} else {
					this.groups.push(updatedGroup);
				}
			}
			return response;
		},
		async createGroup(payload: CreateGroupPayload): Promise<GroupDTO | undefined> {
			if (shouldThrottle('createGroup', 100)) {
				posthog.capture('throttled_action', { action: 'createGroup' });
				return;
			}

			const groupCreatePayload = {
				group: payload.group,
				addressConfirmationToken: payload.addressConfirmationToken
			};

			const response = await apiClient.post<GroupDTO>('/groups/save', groupCreatePayload);
			this.group = response.data;
			this.groups = [response.data, ...this.groups.filter((group) => group.groupNumber !== response.data.groupNumber)];

			if (response.data.groupNumber) {
				this.groupByNumberCache[String(response.data.groupNumber)] = {
					data: response.data,
					ts: Date.now()
				};
			}
			return response.data;
		},

		async updateGroupDetails(groupNumber: number, groupDetails: UpdateGroupDetailsPayload): Promise<GroupDTO> {
			if (!groupNumber) {
				throw new Error('Group number is required.');
			}
			const response = await apiClient.patch<GroupDTO>(`/groups/edit/${groupNumber}`, {
				group: groupDetails
			});
			const updatedGroup = response.data;
			this.group = updatedGroup;
			const existingIndex = this.groups.findIndex((group) => group.groupNumber === updatedGroup.groupNumber);
			if (existingIndex >= 0) {
				this.groups.splice(existingIndex, 1, updatedGroup);
			} else {
				this.groups.push(updatedGroup);
			}
			this.groupByNumberCache[String(groupNumber)] = {
				data: updatedGroup,
				ts: Date.now()
			};
			return updatedGroup;
		},

		async sendGroupOwnerInvite(groupNumber: number, payload: SendGroupOwnerInvitePayload): Promise<SendGroupOwnerInviteResult | undefined> {
			if (shouldThrottle('sendGroupOwnerInvite', 100)) {
				posthog.capture('throttled_action', { action: 'sendGroupOwnerInvite' });
				return { success: false, message: 'Request throttled' };
			}

			try {
				const response = await apiClient.post<Omit<Extract<SendGroupOwnerInviteResult, { success: true }>, 'success'>>(`/groups/${groupNumber}/invite-owner`, payload);
				const data = response.data as Omit<Extract<SendGroupOwnerInviteResult, { success: true }>, 'success'> | null | undefined;
				return { ...(data ?? {}), success: true };
			} catch (error: unknown) {
				posthog.capture('sendGroupOwnerInvite_error', { error: error instanceof Error ? error.message : String(error) });
				const responseStatus = typeof error === 'object' && error && 'response' in error ? (error as { response?: { status?: number } }).response?.status : undefined;
				const responseData = typeof error === 'object' && error && 'response' in error ? (error as { response?: { data?: string | ApiErrorPayload } }).response?.data : undefined;
				const responseMessage = typeof responseData === 'string' ? responseData : (responseData?.message ?? responseData?.error ?? 'Invitation failed');
				return { success: false, status: responseStatus, message: responseMessage, error };
			}
		},
		async fetchUsersByGroup(groupNumber: number | string): Promise<GroupUserDTO[] | undefined> {
			if (shouldThrottle('fetchUsersByGroup', 100)) {
				posthog.capture('throttled_action', { action: 'fetchUsersByGroup' });
				return;
			}

			const response = await apiClient.get<GroupUserDTO[]>(`/groups/${groupNumber}/users`);
			this.users = Array.isArray(response.data) ? response.data : [];
			this.searchUsers = this.users.map((user) => ({
				label: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
				id: user.userNumber,
				firstName: user.firstName ?? null,
				middleName: user.middleName ?? null,
				lastName: user.lastName ?? null,
				city: user.city ?? null,
				state: user.state ?? null,
				userGender: user.gender ?? null,
				primaryPhone: user.primaryPhone ?? null,
				secondaryPhone: user.secondaryPhone ?? null,
				primaryEmail: user.primaryEmail ?? null,
				secondaryEmail: user.secondaryEmail ?? null,
				birthday: user.birthdate ?? null,
				createdOn: user.createdOn ?? null,
				createdBy: user.createdBy ?? null,
				updatedOn: user.updatedOn ?? null,
				username: user.username ?? null,
				usernameConfirmed: user.usernameConfirmed ?? null
			}));

			return this.users;
		},

		async fetchGroupByGroupNumber(groupNum: number | string, { force = false }: { force?: boolean } = {}): Promise<GroupDTO | null> {
			const key = String(groupNum);
			const cached = this.groupByNumberCache[key];
			const now = Date.now();

			if (!force && cached && now - cached.ts < GROUP_DETAIL_TTL_MS) {
				this.group = cached.data;
				return cached.data;
			}

			if (shouldThrottle('fetchGroupByGroupNumber', 100)) {
				posthog.capture('throttled_action', { action: 'fetchGroupByGroupNumber' });
				if (cached?.data) {
					this.group = cached.data;
					return cached.data;
				}
				return this.group;
			}

			const response = await apiClient.get<GroupDTO>(`groups/group/${groupNum}`);
			const data = response.data;
			this.groupByNumberCache[key] = {
				data,
				ts: now
			};
			this.group = data;
			return data;
		},

		async fetchTableUsersByGroup(groupNumber: number | string, filters: TableUserFilters = {}): Promise<unknown[] | undefined> {
			if (shouldThrottle('fetchTableUsersByGroup', 100)) {
				posthog.capture('throttled_action', { action: 'fetchTableUsersByGroup' });
				return;
			}

			const params = Object.fromEntries(
				Object.entries(filters).flatMap(([key, value]) => {
					if (typeof value === 'string') {
						const trimmedValue = value.trim();
						return trimmedValue ? [[key, trimmedValue]] : [];
					}
					return value === null || value === undefined ? [] : [[key, value]];
				})
			);
			const url = `/groups/${groupNumber}/users/table`;
			const response = Object.keys(params).length ? await apiClient.get<unknown[]>(url, { params }) : await apiClient.get<unknown[]>(url);
			return Array.isArray(response.data) ? response.data : [];
		}
	}
});
